/**
 * ListenerAvatar Component
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getInitials } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ListenerAvatar = ({ listener, index, chatMessages = [], onViewStory }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const hasRecentChat = chatMessages.length > 0;
  const [hasStory, setHasStory] = useState(false);
  const [glowColor, setGlowColor] = useState('#FF1493');
  
  // Check if listener has active stories
  useEffect(() => {
    if (!listener.id || !token) return;
    
    const checkStory = async () => {
      try {
        const res = await fetch(`${API_URL}/api/radio/stories/check-glow/${listener.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setHasStory(data.has_stories && data.has_unviewed);
          if (data.has_stories) {
            setGlowColor(data.glow_color || '#FF1493');
          }
        }
      } catch (err) {
        // Silently fail
      }
    };
    checkStory();
  }, [listener.id, token]);

  const handleClick = async () => {
    if (!listener.id) return;
    
    // Don't navigate for simulated/fake listeners (IDs starting with "listener-")
    if (listener.id.startsWith('listener-')) {
      // For simulated listeners, just show a subtle animation or do nothing
      return;
    }
    
    // If has story and onViewStory callback exists, try to show story
    if (hasStory && onViewStory) {
      try {
        const res = await fetch(`${API_URL}/api/radio/stories/user/${listener.id}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          if (data.stories?.length > 0) {
            onViewStory({
              user_id: listener.id,
              user_name: listener.name,
              user_photo: listener.avatar,
              stories: data.stories
            });
            return;
          }
        }
      } catch (err) {
        console.error('Error fetching listener stories:', err);
      }
    }
    
    // No story or fetch failed - go to profile
    navigate(`/profile/${listener.id}`);
  };
  
  return (
    <motion.div
      className="absolute z-20 cursor-pointer"
      style={{ left: `${listener.position.x}%`, top: `${listener.position.y}%` }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: 0.9, 
        scale: 1,
        y: [0, -5, 0],
        x: [0, (index % 2 === 0 ? 3 : -3), 0]
      }}
      transition={{
        opacity: { duration: 0.3, delay: index * 0.08 },
        scale: { duration: 0.3, delay: index * 0.08 },
        y: { duration: 4 + Math.random() * 2, repeat: Infinity, ease: 'easeInOut', delay: index * 0.3 },
        x: { duration: 5 + Math.random() * 2, repeat: Infinity, ease: 'easeInOut', delay: index * 0.4 }
      }}
      whileHover={{ scale: 1.15, opacity: 1 }}
      onClick={handleClick}
      data-testid={`listener-avatar-${listener.id}`}
    >
      {/* Story glow ring */}
      {hasStory && (
        <div 
          className="absolute -inset-0.5 rounded-full animate-pulse"
          style={{ 
            background: `linear-gradient(135deg, ${glowColor}, ${glowColor}88)`,
            boxShadow: `0 0 10px ${glowColor}44`
          }}
        />
      )}
      <div className={`
        relative w-8 h-8 rounded-full border overflow-hidden transition-all duration-300
        ${hasRecentChat ? 'border-[#00F0FF] shadow-[0_0_10px_rgba(0,240,255,0.4)]' : hasStory ? 'border-transparent' : 'border-white/10'}
      `}>
        {listener.avatar ? (
          <img src={listener.avatar} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1a1625] to-[#12101A] flex items-center justify-center">
            <span className="text-white/50 text-xs font-medium">{getInitials(listener.name)}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ListenerAvatar;
